import { useId, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { problemMessage } from "@/api/client";
import type { Animal, CalfCropRequest, CalfCropResponse, CalfRow, Herd } from "@/api/types";
import { newIdempotencyKey, rowErrorsFrom, wasRejected } from "./BulkAnimalsDialog";
import { DialogShell } from "./DialogShell";
import "./BulkAnimalsDialog.css";
import "./CalfCropDialog.css";

/**
 * ef-059-8 — "Calf crop": one dam, one birth date, 1-4 calves, one request
 * (`POST /api/demo/livestock/calf-crop`). Two or more calves are recorded as twins by the server
 * and each calf's birth weight is a weighing dated the BIRTH date — this form just sends them.
 *
 * Same contract as `BulkAnimalsDialog`: one Idempotency-Key per open (rotated only after a 4xx),
 * a synchronous in-flight guard, a server 400 (`calves[i].field`) is pinned back to its calf with
 * every typed value kept, success closes (the hook has already reloaded the roster).
 *
 * "Herd" defaults to the DAM'S herd — a calf is born into its mother's herd, so the farmer only
 * touches it to override.
 */

const MAX_CALVES = 4;
const TAG_MAX = 50;

type CalfField = "tag" | "name" | "sex" | "weight" | "color" | "brand";
type TopField = "damId" | "birthDate" | "sireId" | "herdId";

interface CalfState {
  key: string;
  tag: string;
  name: string;
  sex: string;
  weight: string;
  color: string;
  brand: string;
}

const COLUMNS: { field: CalfField; label: string; server: string; type: "text" | "number" }[] = [
  { field: "tag", label: "Tag", server: "tag", type: "text" },
  { field: "name", label: "Name", server: "name", type: "text" },
  { field: "sex", label: "Sex", server: "sex", type: "text" },
  { field: "weight", label: "Weight (lbs)", server: "birthweight", type: "number" },
  { field: "color", label: "Color", server: "color", type: "text" },
  { field: "brand", label: "Brand", server: "brand", type: "text" },
];

const TOP_LABELS: Record<TopField, string> = {
  damId: "Dam",
  birthDate: "Birth date",
  sireId: "Sire",
  herdId: "Herd",
};

export function CalfCropDialog({
  animals,
  herds,
  onClose,
  onSubmit,
}: {
  /** The whole roster — feeds the dam / sire pickers (active animals only). */
  animals: Animal[];
  /** ACTIVE herds only. */
  herds: Herd[];
  onClose: () => void;
  /** Throws on failure; reloads the roster on success. */
  onSubmit: (body: CalfCropRequest, idempotencyKey: string) => Promise<CalfCropResponse>;
}) {
  const idPrefix = useId();
  const formId = useId();
  const counter = useRef(0);
  const savingRef = useRef(false);
  const keyRef = useRef(newIdempotencyKey());

  function blankCalf(): CalfState {
    counter.current += 1;
    return { key: `calf-${counter.current}`, tag: "", name: "", sex: "", weight: "", color: "", brand: "" };
  }

  const [calves, setCalves] = useState<CalfState[]>(() => [blankCalf()]);
  const [damId, setDamId] = useState("");
  const [sireId, setSireId] = useState("");
  const [herdId, setHerdId] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [calfErrors, setCalfErrors] = useState<Record<string, Partial<Record<CalfField, string>>>>({});
  const [topErrors, setTopErrors] = useState<Partial<Record<TopField, string>>>({});
  const [messages, setMessages] = useState<string[]>([]);

  const parents = useMemo(
    () => animals.filter((a) => a.status === "active").sort((a, b) => a.tag.localeCompare(b.tag)),
    [animals],
  );
  const dam = animals.find((a) => a.id === damId);

  function updateCalf(key: string, field: CalfField, value: string): void {
    setCalves((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)));
    setCalfErrors((prev) => {
      if (!prev[key]?.[field]) return prev;
      const rest = { ...prev[key] };
      delete rest[field];
      return { ...prev, [key]: rest };
    });
  }

  function clearTop(field: TopField): void {
    setTopErrors((prev) => {
      if (!prev[field]) return prev;
      const rest = { ...prev };
      delete rest[field];
      return rest;
    });
  }

  function calfNumber(key: string): number {
    return calves.findIndex((c) => c.key === key) + 1;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (savingRef.current) return;
    setCalfErrors({});
    setTopErrors({});
    setMessages([]);

    // @ui-check: client-side only — mirrors the server so the reason shows without a round trip.
    const top: Partial<Record<TopField, string>> = {};
    const perCalf: Record<string, Partial<Record<CalfField, string>>> = {};
    const lines: string[] = [];
    if (damId === "") {
      top.damId = "Choose the dam.";
      lines.push("Dam: Choose the dam.");
    }
    if (birthDate === "") {
      top.birthDate = "Enter the birth date.";
      lines.push("Birth date: Enter the birth date.");
    }
    for (const calf of calves) {
      const n = calfNumber(calf.key);
      const fail = (field: CalfField, message: string): void => {
        perCalf[calf.key] = { ...perCalf[calf.key], [field]: message };
        lines.push(`Calf ${n}: ${message}`);
      };
      const tag = calf.tag.trim();
      if (tag === "") fail("tag", "Tag is required.");
      else if (tag.length > TAG_MAX) fail("tag", `Tag must be ${TAG_MAX} characters or fewer.`);
      if (calf.weight.trim() !== "") {
        const w = Number(calf.weight);
        if (!Number.isFinite(w) || w <= 0) fail("weight", "Weight must be a number above 0.");
      }
    }
    if (lines.length > 0) {
      setTopErrors(top);
      setCalfErrors(perCalf);
      setMessages(lines);
      return;
    }

    const opt = (v: string): string | undefined => (v.trim() === "" ? undefined : v.trim());
    const body: CalfCropRequest = {
      damId,
      sireId: sireId === "" ? undefined : sireId,
      birthDate,
      herdId: herdId !== "" ? herdId : (dam?.herdId ?? undefined),
      calves: calves.map(
        (c): CalfRow => ({
          tag: c.tag.trim(),
          name: opt(c.name),
          sex: opt(c.sex),
          birthWeight: c.weight.trim() === "" ? undefined : Number(c.weight),
          color: opt(c.color),
          brand: opt(c.brand),
        }),
      ),
    };

    savingRef.current = true;
    setSaving(true);
    try {
      await onSubmit(body, keyRef.current);
      onClose();
    } catch (err) {
      if (wasRejected(err)) keyRef.current = newIdempotencyKey();
      const mapped = rowErrorsFrom(
        err,
        "calves",
        (i) => calves[i]?.key,
        (serverField) => COLUMNS.find((c) => c.server === serverField)?.field,
      );
      const out: string[] = [];
      if (mapped) {
        setCalfErrors(mapped.rows as Record<string, Partial<Record<CalfField, string>>>);
        for (const [calfKey, fields] of Object.entries(mapped.rows)) {
          for (const message of Object.values(fields)) out.push(`Calf ${calfNumber(calfKey)}: ${message}`);
        }
        const nextTop: Partial<Record<TopField, string>> = {};
        for (const [key, message] of Object.entries(mapped.other)) {
          const match = (Object.keys(TOP_LABELS) as TopField[]).find((f) => f.toLowerCase() === key);
          if (match) nextTop[match] = message;
          out.push(match ? `${TOP_LABELS[match]}: ${message}` : message);
        }
        setTopErrors(nextTop);
      }
      setMessages(out.length > 0 ? out : [problemMessage(err, "Could not save this calf crop.")]);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title="Record calf crop"
      onClose={onClose}
      busy={saving}
      restoreFocus
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save calf crop"}
          </button>
        </>
      }
    >
      <form
        id={formId}
        className="entry-dialog__form"
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
        aria-busy={saving}
        data-state={saving ? "saving" : messages.length > 0 ? "error" : "ready"}
      >
        <div className="calf-crop__parents">
          <div className="entry-dialog__field">
            <label htmlFor={`${idPrefix}-dam`}>Dam</label>
            <select
              id={`${idPrefix}-dam`}
              value={damId}
              aria-invalid={topErrors.damId ? true : undefined}
              onChange={(e) => {
                setDamId(e.target.value);
                clearTop("damId");
              }}
            >
              <option value="">Select…</option>
              {parents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.tag}
                </option>
              ))}
            </select>
          </div>
          <div className="entry-dialog__field">
            <label htmlFor={`${idPrefix}-sire`}>Sire (optional)</label>
            <select
              id={`${idPrefix}-sire`}
              value={sireId}
              aria-invalid={topErrors.sireId ? true : undefined}
              onChange={(e) => {
                setSireId(e.target.value);
                clearTop("sireId");
              }}
            >
              <option value="">Unknown</option>
              {parents
                .filter((a) => a.id !== damId)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.tag}
                  </option>
                ))}
            </select>
          </div>
          <div className="entry-dialog__field">
            <label htmlFor={`${idPrefix}-birth`}>Birth date</label>
            <input
              id={`${idPrefix}-birth`}
              type="date"
              value={birthDate}
              aria-invalid={topErrors.birthDate ? true : undefined}
              onChange={(e) => {
                setBirthDate(e.target.value);
                clearTop("birthDate");
              }}
            />
          </div>
          <div className="entry-dialog__field">
            <label htmlFor={`${idPrefix}-herd`}>Herd (optional)</label>
            <select
              id={`${idPrefix}-herd`}
              value={herdId}
              aria-invalid={topErrors.herdId ? true : undefined}
              onChange={(e) => {
                setHerdId(e.target.value);
                clearTop("herdId");
              }}
            >
              <option value="">Same as the dam</option>
              {herds.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="entry-dialog__hint">Two or more calves are recorded as twins.</p>

        <ol className="calf-rows" aria-label="Calves">
          {calves.map((calf, index) => {
            const n = index + 1;
            const errs = calfErrors[calf.key] ?? {};
            return (
              <li
                key={calf.key}
                className="calf-rows__calf"
                data-state={Object.keys(errs).length > 0 ? "invalid" : "ready"}
              >
                <span className="calf-rows__number">Calf {n}</span>
                {COLUMNS.map((c) => {
                  const id = `${idPrefix}-${calf.key}-${c.field}`;
                  return (
                    <div key={c.field} className="entry-dialog__field">
                      <label htmlFor={id} aria-hidden="true">
                        {c.label}
                      </label>
                      <input
                        id={id}
                        aria-label={`${c.label}, calf ${n}`}
                        type={c.type}
                        {...(c.type === "number" ? { min: "0", step: "any", inputMode: "decimal" as const } : {})}
                        value={calf[c.field]}
                        aria-invalid={errs[c.field] ? true : undefined}
                        onChange={(e) => updateCalf(calf.key, c.field, e.target.value)}
                      />
                    </div>
                  );
                })}
                {calves.length > 1 && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    aria-label={`Remove calf ${n}`}
                    onClick={() => setCalves((prev) => prev.filter((x) => x.key !== calf.key))}
                  >
                    Remove
                  </button>
                )}
              </li>
            );
          })}
        </ol>

        <button
          type="button"
          className="btn btn--ghost entry-dialog__add"
          disabled={calves.length >= MAX_CALVES}
          onClick={() => setCalves((prev) => [...prev, blankCalf()])}
        >
          Add calf
        </button>

        {messages.length > 0 && (
          <div className="entry-dialog__error" role="alert">
            {messages.map((m) => (
              <p key={m}>{m}</p>
            ))}
          </div>
        )}

      </form>
    </DialogShell>
  );
}
